/* =============================================================================
   UTILS / HELPERS — Pure-Functions + ein DOM-Touch (showToast)
   =============================================================================
   Erstes echtes ES-Modul der Grill-&-Green-Codebase (Phase 1.1).
   Identische Definitionen wie bisher in app.js — die werden dort vorerst noch
   parallel gehalten, damit klassisch geladene Scripts (z.B. die inline-Skripte
   in index.html oder seed-test-data.html) ohne Modul-Auflösung weiterlaufen.

   Backward-Compat-Hook (siehe unten): die Helpers werden zusätzlich ans
   `window`-Object gehängt. Damit greifen sowohl ES-Module-Imports als auch
   inline-onclick-Handler aus index.html / app.js auf dieselbe Implementierung.

   Sobald app.js in Phase 1.2+ in Module aufgeteilt wird, ziehen die anderen
   Module ihre Helpers per `import` aus dieser Datei.
============================================================================ */

// S77-A: geteilte Cent/Euro-Normalisierungs-Primitive aus dem Edge-Function-
// Spiegel. `lineTotalToCents` erkennt Snake-Cent-Integer (>=100) vs. Euro-Float
// — exakt dieselbe Heuristik, die `process-mail-outbox` für Queue-Mails nutzt.
// Damit rendert der JS-Direct-Send-Pfad (Receipt-Mails) Beträge identisch zum
// Edge-Pfad. Anti-Drift: Memory `gg-edge-function-source-drift`.
import { lineTotalToCents } from '../core/edge-mail-helpers.js';

/** ID-Generator: Prefix + Base36-Zeitstempel + 5-Zeichen-Random-Suffix. */
export function uid(prefix = 'r') {
  return prefix + '_' + Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
}

/* -----------------------------------------------------------------------------
   Timezone-Handling (Phase 28, S26 — H-07-Vollausbau)

   Hintergrund: Das deutsche Steuerrecht (GoBD/KassenSichV/DSFinV-K) erwartet
   Tagesgrenzen in deutscher Lokalzeit (Europe/Berlin). Der Code arbeitete
   bisher mit `new Date()` + `d.getMonth()/getDate()` — implizit Browser-TZ.
   Bei Bedienern in einer anderen TZ (Cloud-Browser, Remote-Admin) oder bei
   Server-Cron-Jobs (UTC) führt das zu falschen Tagesabschlüssen.

   API-Design (hybrid, S26):
     - `DEFAULT_TIMEZONE` = 'Europe/Berlin' als Hard-Coded Fallback (auch wenn
       der State noch nicht geladen ist, z.B. beim ersten Render).
     - `getRestaurantTimezone()` liest aus `_state.restaurant.settings.timezone`
       (über Storage-Internal-State). Falls nicht gesetzt: DEFAULT_TIMEZONE.
     - Alle Date-Helper akzeptieren optional ein `{ timeZone }`-Objekt. Ohne
       Argument: `getRestaurantTimezone()`. Damit bleiben ~150 bestehende
       Aufrufstellen unverändert UND wir können in Tests gezielt overriden.

   Implementation: `Intl.DateTimeFormat('en-CA', { timeZone, year, month, day })`
   ist die Standard-Methode in modernen Browsern. 'en-CA' produziert immer
   das ISO-Format `YYYY-MM-DD`.
----------------------------------------------------------------------------- */

export const DEFAULT_TIMEZONE = 'Europe/Berlin';

/** Liest die aktuelle Restaurant-Timezone aus dem App-State.
 *  Fallback: DEFAULT_TIMEZONE ('Europe/Berlin'). Defensiver Read — wenn der
 *  State noch nicht initialisiert ist (z.B. beim ersten Render), gibt es
 *  trotzdem einen sinnvollen Default. */
export function getRestaurantTimezone() {
  try {
    if (typeof window !== 'undefined' && window.App && window.App.storage) {
      const s = window.App.storage._getInternalState && window.App.storage._getInternalState();
      const tz = s && s.settings && s.settings.timezone;
      if (typeof tz === 'string' && tz) return tz;
    }
  } catch (_) { /* defensive — Falls Storage noch nicht da ist */ }
  return DEFAULT_TIMEZONE;
}

/** Helper: extrahiert `timeZone` aus dem optionalen opts-Argument oder
 *  fällt auf `getRestaurantTimezone()` zurück. */
function _tz(opts) {
  if (opts && typeof opts === 'object' && typeof opts.timeZone === 'string' && opts.timeZone) {
    return opts.timeZone;
  }
  return getRestaurantTimezone();
}

/** ISO-Datum → langes deutsches Format ("Di., 14. Mai 2026"). TZ-aware. */
export function fmtDate(iso, opts) {
  if (!iso) return '';
  const d = new Date(iso + 'T12:00:00Z'); // 12:00 UTC liegt sicher mitten im
  // jeweiligen Lokal-Tag — keine Off-by-one-Falle an Tagesgrenze.
  return d.toLocaleDateString('de-DE', {
    weekday: 'short', day: '2-digit', month: 'long', year: 'numeric',
    timeZone: _tz(opts)
  });
}

/** ISO-Datum → kompaktes Format ("14.05.26"). TZ-aware. */
export function fmtDateShort(iso, opts) {
  if (!iso) return '';
  const d = new Date(iso + 'T12:00:00Z');
  return d.toLocaleDateString('de-DE', {
    day: '2-digit', month: '2-digit', year: '2-digit',
    timeZone: _tz(opts)
  });
}

/** ISO-Datum → reine Tagesnummer als String ("14"). */
export function dayNum(iso) {
  if (!iso) return '';
  return iso.slice(8, 10);
}

/** ISO-Datum → Kurz-Monatsname ("Mai"). TZ-aware. */
export function monthShort(iso, opts) {
  if (!iso) return '';
  const d = new Date(iso + 'T12:00:00Z');
  return d.toLocaleDateString('de-DE', { month: 'short', timeZone: _tz(opts) });
}

/** Interner Helper: gegebenes Date → YYYY-MM-DD in der angegebenen IANA-Timezone.
 *  Verwendet en-CA-Locale (immer ISO-Format YYYY-MM-DD) + Intl.DateTimeFormat. */
function _dateToIsoInTimezone(date, tz) {
  // Intl.DateTimeFormat ist die einzige browserweit verlässliche Methode,
  // ein Date in eine fremde IANA-Timezone zu projizieren.
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit'
  });
  // en-CA liefert direkt 'YYYY-MM-DD' (manche Locale-Varianten escapen
  // Bindestriche zu Non-Breaking-Hyphens; normalisieren).
  return fmt.format(date).replace(/[‐-―]/g, '-');
}

/** Heutiges Datum als ISO-String (YYYY-MM-DD) in der konfigurierten
 *  Restaurant-Timezone. */
export function todayISO(opts) {
  return _dateToIsoInTimezone(new Date(), _tz(opts));
}

/** Gegebenes Date → YYYY-MM-DD in der konfigurierten Restaurant-Timezone.
 *  S60 FIX.2 — vermeidet UTC-Slice-Falle wie `.toISOString().slice(0,10)`,
 *  die bei lokaler Mitternacht +/- DST das Datum auf den Vortag drücken kann.
 *  Anwendungsfälle: pickup_date für Orders, Buchungstag für Z-Bons, Stats. */
export function dateToISO(date, opts) {
  return _dateToIsoInTimezone(date instanceof Date ? date : new Date(date), _tz(opts));
}

/** Morgiges Datum als ISO-String (YYYY-MM-DD) in der konfigurierten
 *  Restaurant-Timezone. */
export function tomorrowISO(opts) {
  const tz = _tz(opts);
  const today = _dateToIsoInTimezone(new Date(), tz);
  // String-Increment: parse YYYY-MM-DD, +1 Tag in UTC, dann wieder in Berlin
  // projizieren. UTC-Math ist DST-immun, weil wir hier mit DATEN rechnen,
  // nicht mit Stunden.
  const [y, m, d] = today.split('-').map(Number);
  const next = new Date(Date.UTC(y, m - 1, d) + 86400000);
  // Achtung: Date.UTC(y,m-1,d) + 86400000 gibt UTC-Mitternacht des Folgetages.
  // Diese wird in Berlin als 01:00 (Winter) oder 02:00 (Sommer) angezeigt,
  // also IMMER derselbe Tag — keine DST-Falle. (Wir formatieren mit timeZone-
  // Option, deshalb ist die UTC-Position egal solange wir nicht über die
  // Berliner Tagesgrenze rutschen.)
  return _dateToIsoInTimezone(next, tz);
}

/** Gestriges Datum als ISO-String (YYYY-MM-DD) in der konfigurierten
 *  Restaurant-Timezone. Spiegelbild zu `tomorrowISO` (UTC-Datums-Math ist
 *  DST-immun, weil wir mit DATEN rechnen, nicht mit Stunden). Anwendung:
 *  S93-B Z-Bon-Auto-Archivierung des Vortags beim ersten Cockpit-Aufruf. */
export function yesterdayISO(opts) {
  const tz = _tz(opts);
  const today = _dateToIsoInTimezone(new Date(), tz);
  const [y, m, d] = today.split('-').map(Number);
  const prev = new Date(Date.UTC(y, m - 1, d) - 86400000);
  return _dateToIsoInTimezone(prev, tz);
}

/** "HH:MM" → Minuten seit Mitternacht. */
export function timeToMinutes(t) {
  const [h, m] = t.split(':').map(Number);
  return h * 60 + m;
}

/** Minuten seit Mitternacht → "HH:MM". */
export function minutesToTime(min) {
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/** Zahl → deutsches Währungsformat ("12,50 €"). */
export function fmtEUR(num) {
  return (Math.round(num * 100) / 100).toFixed(2).replace('.', ',') + ' €';
}

/* ---------------------------------------------------------------------------
 * Preorder-Normalisierung (v0.19.0).
 *
 * Seit v0.19.0 schreibt die Customer-Form Vorbestellungen im v2-Format
 *     { version: 2, items: [<line>...], specialRequests, subtotal }
 * in `reservations.preorder` (jsonb). Vor v0.19.0 war es ein flaches Array
 *     [{ id, name, price, qty }]
 * Damit Mail-Templates, CSV-Export, Cockpit-Listen, Tagesblatt etc. beide
 * Formate gleich anzeigen können, normalisieren wir hier auf eine einzige
 * Abrechnungs-Liste: { displayName, qty, lineTotal, note? }.
 * ------------------------------------------------------------------------- */

/** S77-A: Zeilensumme einer Position in Euro auflösen — deckt ALLE Feldformate
 *  ab, die in der Codebase vorkommen:
 *    - `lineTotal`        (camelCase-Euro, v2 Customer-Form)
 *    - `line_total_cents` (snake-Cents, Receipt-Snapshot aus order_items, S77-Bug)
 *    - `line_total`       (snake-Cents, alt)
 *  Fallback: Einzelpreis × Menge, mit denselben Cent/Euro-Varianten
 *    (`unitPrice` / `unit_price` / `unit_price_cents` / `price`).
 *  Nutzt die geteilte `lineTotalToCents`-Primitive (Cent/Euro-Erkennung wie
 *  Edge-Function) und gibt Euro zurück, weil die Konsumenten `fmtEUR()` nutzen.
 *  Vorher las der v2-Branch nur `it.lineTotal` → snake-Cents-Snapshots wurden
 *  zu `0` → Receipt-Mail zeigte `(0,00 €)` (S76-Browser-Befund). */
function resolveLineTotalEur(it, qty) {
  const directCents = lineTotalToCents(it.lineTotal ?? it.line_total_cents ?? it.line_total);
  if (directCents > 0) return directCents / 100;
  const unitCents = lineTotalToCents(it.unitPrice ?? it.unit_price ?? it.unit_price_cents ?? it.price);
  return unitCents > 0 ? (unitCents * qty) / 100 : 0;
}

/** Liefert eine flache Liste der Bestellpositionen — funktioniert für
 *  beide Formate (v1-Array und v2-Object). Bei v2 wird zusätzlich der
 *  "OHNE …", "Brötchen: …", "Patty: …" usw. in `note` zusammengefasst. */
export function normalizePreorderItems(preorder) {
  if (!preorder) return [];
  // v1: flat array
  if (Array.isArray(preorder)) {
    return preorder.map(p => ({
      displayName: p.name || '',
      qty: Number(p.qty) || 1,
      lineTotal: resolveLineTotalEur(p, Number(p.qty) || 1),
      note: ''
    }));
  }
  // v2: object with items[]
  if (preorder && typeof preorder === 'object' && Array.isArray(preorder.items)) {
    return preorder.items.map(it => {
      const notes = [];
      if (it.bun)      notes.push('Brötchen: ' + it.bun.name);
      if (it.patty)    notes.push('Patty: ' + it.patty.name);
      if (it.dressing) notes.push('Dressing: ' + it.dressing.name);
      const removed = Array.isArray(it.removedIngredients) ? it.removedIngredients : [];
      const visibleRemoved = removed.filter(x => x !== '__patty__');
      if (visibleRemoved.length) notes.push('OHNE: ' + visibleRemoved.join(', '));
      if (removed.includes('__patty__') && !it.patty) notes.push('OHNE Patty');
      if (Array.isArray(it.extras) && it.extras.length) {
        notes.push('Extras: ' + it.extras.map(e => e.name).join(', '));
      }
      // S229 T1 — sides[] (Mehrfach) tolerant lesen, Legacy-side (Objekt) als Fallback.
      const sidesArr = Array.isArray(it.sides) ? it.sides : (it.side ? [it.side] : []);
      if (sidesArr.length) {
        let sideNote = 'MIT Beilage: ' + sidesArr.map(s => s.name).filter(Boolean).join(', ');
        if (Array.isArray(it.sideSauces) && it.sideSauces.length) {
          sideNote += ' (Soße: ' + it.sideSauces.map(e => e.name).join(', ') + ')';
        }
        notes.push(sideNote);
      }
      if (Array.isArray(it.sauces) && it.sauces.length) {
        notes.push('Soßen: ' + it.sauces.map(e => e.name).join(', '));
      }
      return {
        displayName: it.name || '',
        qty: Number(it.qty) || 1,
        lineTotal: resolveLineTotalEur(it, Number(it.qty) || 1),
        note: notes.join(' · ')
      };
    });
  }
  return [];
}

/** Wahrheitswert: trägt der Snapshot tatsächlich Positionen, die
 *  `normalizePreorderItems` rendern würde? S81-A: Cockpit-erfasste Dine-In-
 *  Orders haben `preorder_snapshot = {}` (leeres Object, **truthy** → der
 *  naive `snap || fallback`-Check rauscht durch). Dieser Guard zählt `{}`,
 *  `null`, leeres v1-Array und v2 mit leerem `items[]` als „nicht renderbar". */
export function snapshotHasRenderableItems(snap) {
  return normalizePreorderItems(snap).length > 0;
}

/** Baut aus einer `order_items`-Liste (camelCase aus `storage.listItems` /
 *  defensiv auch snake_case) einen v2-`preorderSnapshot`, den
 *  `normalizePreorderItems` rendern kann. S81-A: Cockpit-Dine-In-Orders haben
 *  ihre Positionen nur in der `order_items`-Tabelle, nicht im Snapshot — dieser
 *  Helfer ist die Single-Source-Brücke (dasselbe Muster wie der Detail-Modal-/
 *  Bon-Renderer in `orders.js` `orderMarkPaid`). Liefert `null`, wenn keine
 *  renderbaren Positionen vorliegen (kein leerer Snapshot-Müll im Cache). */
export function itemsToPreorderSnapshot(orderItems) {
  if (!Array.isArray(orderItems) || orderItems.length === 0) return null;
  const items = orderItems.map(it => {
    // S88-A: Customizations (order_items.customizations) lesbar an die Note
    // anhaengen — sonst zeigt der Kassenbon/die Dine-In-Card nur den Item-Namen
    // ("1x SIMPLE CHEESE") statt der Individualisierung ("Bun: brioche · Extras:
    // rinderbacon · ohne: ketchup"). formatCustomizations ist nameOf-robust
    // (String ODER {name}-Objekt). Identische Quelle wie die Edge-Mail (v36).
    const cust = it.customizations || it.customization || null;
    const baseNote = it.notes ?? it.note ?? (cust && cust.note) ?? '';
    const custText = formatCustomizations(cust).join(' · ');
    const note = [custText, baseNote].filter(Boolean).join(' · ');
    return {
      name:             it.nameSnapshot ?? it.name_snapshot ?? it.name ?? '',
      qty:              Number(it.qty ?? it.quantity) || 1,
      unit_price_cents: Number(it.unitPriceCents ?? it.unit_price_cents ?? 0),
      line_total_cents: Number(it.lineTotalCents ?? it.line_total_cents ?? 0),
      tax_rate:         Number(it.taxRate ?? it.tax_rate ?? 7),
      note
    };
  });
  return { version: 2, items };
}

/** Summe einer Vorbestellung (v1 oder v2). */
export function preorderTotal(preorder) {
  if (preorder && typeof preorder === 'object' && !Array.isArray(preorder)
      && typeof preorder.subtotal === 'number') {
    return preorder.subtotal;
  }
  return normalizePreorderItems(preorder).reduce((s, it) => s + (it.lineTotal || 0), 0);
}

/** Wahrheitswert: existiert mindestens 1 Position? */
export function preorderHasItems(preorder) {
  return normalizePreorderItems(preorder).length > 0;
}

/** Optionaler Sonderwunsch-/Allergie-Text aus v2 (v1 hatte keinen). */
/**
 * Render-Helper für die `customizations`-JSON-Struktur einer order_item-Zeile.
 * Beispielinput:
 *   {
 *     bun:      { id, name, extra },
 *     side:     { id, name, price },
 *     patty:    null,
 *     extras:   [{ id, name, price }, ...],
 *     sauces:   [],
 *     dressing: null,
 *     sideSauces: [{ id, name, price }, ...],
 *     removedIngredients: ['salat', ...]
 *   }
 *
 * Output: ein Array von Zeilen-Strings für Anzeige:
 *   ['Bun: brioche', 'Beilage: süßkartoffelpommes', 'Extras: cheddar', ...]
 * `null`-Werte und leere Arrays werden ausgelassen.
 * Wird sowohl im Order-Detail-Modal als auch im Bon-Renderer benutzt.
 */
export function formatCustomizations(cust) {
  if (!cust || typeof cust !== 'object') return [];
  const out = [];

  // Label-Mapping (deutsche Beschriftung). Reihenfolge bestimmt die Render-
  // Reihenfolge im UI.
  const LABELS = [
    ['bun',                'Bun'],
    ['patty',              'Patty'],
    ['milk',               'Milch'],
    ['side',               'Beilage'],
    ['extras',             'Extras'],
    ['sauces',             'Soßen'],
    ['dressing',           'Dressing'],
    ['sideSauces',         'Soße zur Beilage'],
    ['removedIngredients', 'ohne']
  ];

  const nameOf = (v) => {
    if (v == null) return '';
    if (typeof v === 'string') return v;
    if (typeof v === 'object') return String(v.name || v.displayName || v.title || v.id || '').trim();
    return String(v);
  };

  // S89-Polish: „ohne" liest sich ohne Doppelpunkt + kapitalisiert natürlicher
  // („ohne Ketchup" statt „ohne: ketchup"). Gilt nur für removedIngredients.
  const cap = (s) => { const t = String(s || '').trim(); return t ? t.charAt(0).toUpperCase() + t.slice(1) : t; };
  for (const [key, label] of LABELS) {
    // S229 T1 — Beilage: neue Mehrfach-Liste sides[] bevorzugt, Legacy-side (Objekt)
    // als Fallback. Der 'side'-Slot deckt beide ab (Reihenfolge bleibt erhalten).
    if (key === 'side') {
      const sidesArr = Array.isArray(cust.sides) ? cust.sides : (cust.side ? [cust.side] : []);
      const names = sidesArr.map(nameOf).filter(Boolean);
      if (names.length) out.push(label + ': ' + names.join(', '));
      continue;
    }
    const v = cust[key];
    if (v == null) continue;
    if (Array.isArray(v)) {
      const names = v.map(nameOf).filter(Boolean);
      if (names.length === 0) continue;
      out.push(key === 'removedIngredients'
        ? 'ohne ' + names.map(cap).join(', ')
        : label + ': ' + names.join(', '));
    } else if (typeof v === 'object') {
      const n = nameOf(v);
      if (!n) continue;
      out.push(label + ': ' + n);
    } else if (typeof v === 'string' && v.trim()) {
      out.push(key === 'removedIngredients' ? 'ohne ' + cap(v) : label + ': ' + v);
    }
  }

  // Zusätzliche freie Felder, die nicht im LABELS-Mapping stehen (z.B. note),
  // werden separat ausgegeben falls nicht-leer und nicht-bereits-gerendert.
  const knownKeys = new Set(LABELS.map(([k]) => k));
  knownKeys.add('sides');   // S229 T1 — über den 'side'-Slot gerendert, nicht doppeln
  for (const [key, val] of Object.entries(cust)) {
    if (knownKeys.has(key)) continue;
    if (val == null) continue;
    if (key === 'note') continue; // wird separat behandelt
    if (key.charAt(0) === '_') continue; // S169 — interne Meta (z. B. _dealMeta:
                                         // Deal-Reporting-Klammer) wird NIE gerendert;
                                         // bleibt als Daten am order_item erhalten.
    const n = nameOf(val);
    if (n) out.push(key + ': ' + n);
  }

  return out;
}

/**
 * Kategorie-spezifische Customizations-Sortierung (KDS-Reihenfolge).
 * Die Küche liest in einer bestimmten Reihenfolge — diese muss der Display
 * widerspiegeln, damit das Auge schnell die wichtigste Info findet.
 *
 * Reihenfolge (laut Jonas, Session 20):
 *   Burger:  Patty · Extras · Bun · Ohne · Beilage · Soße
 *   Bowls:   Ohne · Patty · Extras · Dressing · Beilage · Soße
 *   Salate:  Ohne · Extras · Beilage · Soße
 *   Default: wie formatCustomizations()
 */
export function formatCustomizationsByCategory(cust, category) {
  if (!cust || typeof cust !== 'object') return [];

  const cat = String(category || '').toLowerCase();
  let order;
  if (cat.includes('burger'))       order = ['patty', 'extras', 'bun', 'removedIngredients', 'side', 'sauces'];
  else if (cat.includes('bowl'))    order = ['removedIngredients', 'patty', 'extras', 'dressing', 'side', 'sauces'];
  else if (cat.includes('salat') || cat.includes('salad'))
                                    order = ['removedIngredients', 'extras', 'side', 'sauces'];
  else                              order = null;

  if (!order) return formatCustomizations(cust);

  const LABELS = {
    bun:                 'Bun',
    patty:               'Patty',
    milk:                'Milch',
    side:                'Beilage',
    extras:              'Extras',
    sauces:              'Soßen',
    dressing:            'Dressing',
    sideSauces:          'Soße zur Beilage',
    removedIngredients:  'ohne'
  };

  const nameOf = (v) => {
    if (v == null) return '';
    if (typeof v === 'string') return v;
    if (typeof v === 'object') return String(v.name || v.displayName || v.title || v.id || '').trim();
    return String(v);
  };

  const cap = (s) => { const t = String(s || '').trim(); return t ? t.charAt(0).toUpperCase() + t.slice(1) : t; };
  const out = [];
  for (const key of order) {
    // S229 T1 — Beilage-Slot: Mehrfach-Liste sides[] bevorzugt, Legacy-side (Objekt)
    // als Fallback; Soße-zur-Beilage bleibt EINE Sub-Info an der Beilagen-Zeile.
    if (key === 'side') {
      const sidesArr = Array.isArray(cust.sides) ? cust.sides : (cust.side ? [cust.side] : []);
      const names = sidesArr.map(nameOf).filter(Boolean);
      if (names.length) {
        let line = LABELS.side + ': ' + names.join(', ');
        if (Array.isArray(cust.sideSauces) && cust.sideSauces.length) {
          const ss = cust.sideSauces.map(nameOf).filter(Boolean).join(', ');
          if (ss) line += ' (Soße: ' + ss + ')';
        }
        out.push(line);
      }
      continue;
    }
    const v = cust[key];
    if (v == null) continue;
    const label = LABELS[key];
    if (Array.isArray(v)) {
      const names = v.map(nameOf).filter(Boolean);
      if (names.length) out.push(key === 'removedIngredients'
        ? 'ohne ' + names.map(cap).join(', ')
        : label + ': ' + names.join(', '));
    } else if (typeof v === 'object') {
      // Beilage (inkl. Soße-zur-Beilage) wird oben über den 'side'-Slot behandelt.
      const n = nameOf(v);
      if (n) out.push(label + ': ' + n);
    } else if (typeof v === 'string' && v.trim()) {
      out.push(key === 'removedIngredients' ? 'ohne ' + cap(v) : label + ': ' + v);
    }
  }
  return out;
}

export function preorderSpecialRequests(preorder) {
  if (preorder && typeof preorder === 'object' && !Array.isArray(preorder)
      && typeof preorder.specialRequests === 'string') {
    return preorder.specialRequests.trim();
  }
  return '';
}

// -----------------------------------------------------------------------------
// Time-/Display-Helpers (S66 Refactor: zentralisiert aus orders.js)
// -----------------------------------------------------------------------------

/** "HH:MM:SS" oder "HH:MM" → "HH:MM"; sonst Rohwert.
 *  S66: aus src/views/admin/orders.js heraufgezogen, damit alle Views
 *  dieselbe Time-Slice-Logik nutzen (Single-Source). */
export function shortTime(t) {
  if (!t) return '';
  if (typeof t === 'string' && t.length >= 5) return t.slice(0, 5);
  return String(t);
}

/** ISO-Timestamp → lokalisierte de-DE Kurzanzeige (dd.MM.YY HH:MM).
 *  S66: aus src/views/admin/orders.js heraufgezogen — wird auch in
 *  Order-Detail-Modals + Audit-Timeline + Archive verwendet. */
export function formatTs(iso) {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' });
  } catch (_) { return String(iso); }
}

/** Minuten-Diff → menschen-lesbar ("in 5 min" / "+12 min überfällig" / "in 2h 15m").
 *  S66: aus src/views/admin/orders.js heraufgezogen — KDS-Timer-Anzeige + Bar-Timer
 *  und Liefer-Cockpit nutzen das gleiche Pattern. */
export function formatMinutes(mins) {
  if (mins == null) return '';
  if (mins <= 0) return `+${-mins} min überfällig`;
  if (mins < 60)  return `in ${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `in ${h}h ${m}m`;
}

/** HTML-Special-Chars escapen für sicheres innerHTML-Embedding. */
export function htmlEscape(s) {
  if (s === null || s === undefined) return '';
  return String(s).replace(/[&<>"']/g, c => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
  }[c]));
}

/**
 * Toast-Nachricht unten in der UI. Nicht ganz "pure" (DOM-Zugriff), aber
 * gehört konzeptionell zu den App-weit verfügbaren UI-Helpers.
 */
export function showToast(msg, type = '') {
  const t = document.getElementById('toast');
  if (!t) return;
  t.textContent = msg;
  t.className = 'toast show ' + type;
  clearTimeout(t._timer);
  t._timer = setTimeout(() => { t.className = 'toast ' + type; }, 2500);
}

/**
 * Phase 41 (S51): Defense-in-Depth-Gate für Schreibvorgänge in den
 * Bearbeitungs-Editoren (menu-editor, layout-editor). Liefert true, wenn
 * der eingeloggte User in dem Scope schreiben darf. Bei false wird ein
 * Toast gezeigt und der Caller soll abbrechen.
 *
 * Source-of-Truth bleibt PERMISSIONS in public/adapters/auth.js. Die
 * eigentliche Schutz-Schicht ist die DB-RLS (Migration phase_41) — dieses
 * Client-Gate verhindert nur die Roundtrip + zeigt User die Begründung.
 *
 * @param {string} scope — z.B. 'menu', 'tables', 'settings.restaurant'
 * @param {string} [actionHint] — kurzer Hinweis für den Toast (z.B. „Gericht löschen")
 * @returns {boolean} true wenn der Caller weitermachen darf
 */
export function requireEditPermission(scope, actionHint) {
  const auth = (typeof window !== 'undefined' && window.App && window.App.auth) ? window.App.auth : null;
  if (!auth || typeof auth.canEdit !== 'function') {
    // Pre-Bootstrap oder Tests ohne Auth-Modul: nicht blockieren.
    return true;
  }
  if (auth.canEdit(scope)) return true;
  const role = (typeof auth.getRole === 'function') ? auth.getRole() : null;
  const reason = actionHint
    ? `${actionHint} ist nur für Inhaber/Manager freigegeben.`
    : 'Für diese Aktion fehlt dir die Berechtigung.';
  showToast(reason + (role ? ` (deine Rolle: ${role})` : ''), 'error');
  // eslint-disable-next-line no-console
  console.warn('[permissions] requireEditPermission denied:', { scope, role, actionHint });
  return false;
}

/** Aktuelle Uhrzeit auf nächsten 30-Min-Slot ABGERUNDET (z.B. 14:37 → 14:30).
 *  TZ-aware: liest die aktuelle Stunde/Minute in der Restaurant-Timezone, nicht
 *  in der Browser-TZ. */
export function nowSnappedToHalfHour(opts) {
  const tz = _tz(opts);
  // Intl.DateTimeFormat mit hour12=false + 24h-Format produziert 'HH:MM'.
  const fmt = new Intl.DateTimeFormat('en-GB', {
    timeZone: tz, hour: '2-digit', minute: '2-digit', hour12: false
  });
  const parts = fmt.formatToParts(new Date());
  const h = parts.find(p => p.type === 'hour')?.value || '00';
  const mRaw = parts.find(p => p.type === 'minute')?.value || '00';
  const m = String(Math.floor(parseInt(mRaw, 10) / 30) * 30).padStart(2, '0');
  // 'en-GB' kann '24:00' liefern (Mitternacht) — wir normalisieren auf '00:00'.
  return (h === '24' ? '00' : h) + ':' + m;
}

/** Aktuelle Uhrzeit als 'HH:MM' in der Restaurant-Timezone (ohne Snapping).
 *  Pendant zu todayISO() für Zeit-Anteile (z.B. für `pickup_time = jetzt`). */
export function nowTimeHHMM(opts) {
  const tz = _tz(opts);
  const fmt = new Intl.DateTimeFormat('en-GB', {
    timeZone: tz, hour: '2-digit', minute: '2-digit', hour12: false
  });
  const parts = fmt.formatToParts(new Date());
  const h = parts.find(p => p.type === 'hour')?.value || '00';
  const m = parts.find(p => p.type === 'minute')?.value || '00';
  return (h === '24' ? '00' : h) + ':' + m;
}

/** Erkennt, ob `iso` (YYYY-MM-DD) ein DST-Übergangs-Tag in `tz` ist.
 *  Liefert:
 *    null                                — kein DST-Tag
 *    { type: 'spring', missingHour: 2 }  — März: 02:00 fehlt
 *    { type: 'fall',   doubledHour: 2 }  — Oktober: 02:00 doppelt
 *
 *  Verwendung: r-time-Dropdowns (customer-form, takeaway-form) markieren
 *  die Doppelstunde am Oktober-Tag mit Hinweis. */
export function detectDstTransition(iso, opts) {
  if (!iso) return null;
  const tz = _tz(opts);
  // Strategie: UTC-Offset zu Beginn und Ende des angegebenen Tages messen.
  // Wenn die UTC-Offset-Differenz != 0, ist es ein Übergangstag.
  const dayStart = new Date(iso + 'T00:00:00Z');
  const dayEnd   = new Date(iso + 'T23:59:00Z');
  // Trick: dateString in `tz` für beide Punkte parsen → reale lokale Uhrzeit
  // entspricht UTC-Punkt + Offset. Differenz = DST-Sprung.
  const offsetMin = (date) => {
    const localStr = date.toLocaleString('en-US', {
      timeZone: tz, hour12: false,
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit'
    });
    // localStr: "MM/DD/YYYY, HH:MM:SS"
    const [d, t] = localStr.split(', ');
    const [mo, da, yr] = d.split('/').map(Number);
    const [h, mi, s] = t.split(':').map(Number);
    const localUtc = Date.UTC(yr, mo - 1, da, h, mi, s);
    return Math.round((localUtc - date.getTime()) / 60000);
  };
  const offStart = offsetMin(dayStart);
  const offEnd   = offsetMin(dayEnd);
  const delta = offEnd - offStart;
  if (delta === 60)   return { type: 'spring', missingHour: 2 };
  if (delta === -60)  return { type: 'fall',   doubledHour: 2 };
  return null;
}

/**
 * S134c (Befund 3) — Anzeige-Name eines Gastes für Bon + Mail-Anrede.
 * Liefert "" für Platzhalter, die der Cockpit-Theke-/Tisch-Flow erzeugt
 * (z.B. "Theke 2026-06-12-005", "Tisch 5") oder reine Order-Nummern — damit
 * keine Anrede wie „Hallo Theke 2026-…" entsteht. Echte Namen bleiben erhalten.
 */
export function displayGuestName(raw) {
  const s = (raw == null ? '' : String(raw)).trim();
  if (!s) return '';
  if (/^(theke|tisch)\b/i.test(s)) return '';          // "Theke …" / "Tisch …"
  if (/^\d{4}-\d{2}-\d{2}-\d+$/.test(s)) return '';     // reine Order-Nummer
  return s;
}

/* -----------------------------------------------------------------------------
   Backward-Compat: window-Globals für inline-onclick-Handler aus index.html
   und app.js.

   Warum nötig? Inline-Handler wie `onclick="renderOccupancy(); occupancyDate=...;"`
   können Module-Scope-Bindings nicht auflösen — sie laufen im globalen Scope.
   Solange app.js noch als klassisches Script geladen wird, sind diese Helpers
   dort schon global definiert; sobald app.js auf ES-Module umgestellt wird,
   übernimmt der hier folgende Block die Aufgabe, sie weiterhin als globale
   `window.X`-Symbole bereitzustellen.

   Falls app.js noch im klassischen Modus läuft, sind die folgenden Zeilen
   harmlos: sie überschreiben das (identische) Symbol mit derselben Funktion.
----------------------------------------------------------------------------- */
if (typeof window !== 'undefined') {
  Object.assign(window, {
    uid, fmtDate, fmtDateShort, dayNum, monthShort,
    todayISO, tomorrowISO,
    timeToMinutes, minutesToTime, fmtEUR,
    htmlEscape, showToast, nowSnappedToHalfHour, nowTimeHHMM,
    normalizePreorderItems, preorderTotal, preorderHasItems, preorderSpecialRequests,
    formatCustomizations, displayGuestName,
    // Phase 28 (S26): TZ-Helper auch als window-Globals für Inline-Handler.
    getRestaurantTimezone, detectDstTransition, DEFAULT_TIMEZONE
  });
}
